type FilterFn<T> = Parameters<Array<T>["filter"]>[0];

export abstract class FilterUtil {
  /**
   * Filter an array to ensure its entries are unique by reference.
   *
   * @example
   * const filtered = [1, 1, 1, 1, 2, 3].filter(FilterUtil.unique);
   * // filtered === [1, 2, 3]
   */
  public static unique<T>(value: T, index: number, self: T[]) {
    return self.indexOf(value) === index;
  }

  /**
   * Filter an array to ensure its entries are unique according to a specific property.
   *
   * @example
   * const filtered = [
   *  { id: 1 },
   *  { id: 2 },
   *  { id: 3 },
   *  { id: 1 }
   * ].filter(FilterUtil.uniqueBy(value => value.id));
   * // filtered === [ { id: 1 }, { id: 2 }, { id: 3 } ]
   */
  public static uniqueBy<T, M>(matcher: (value: T) => M): FilterFn<T> {
    return (value: T, index: number, self: T[]) => {
      const thisEquiv = matcher(value);
      return self.findIndex(v => matcher(v) === thisEquiv) === index;
    };
  }

  public static exceptValue<T>(excludedValue: T): FilterFn<T> {
    return (value: T) => value !== excludedValue;
  }

  public static exists<T>(value: T): value is NonNullable<T> {
    return typeof value !== "undefined" && value !== null;
  }
}
